import type {DropdownRepository} from '../../hooks/useCopilotRepoFetching'
import {CopilotAssignmentForm, type CopilotAssignmentFormRef} from './CopilotAssignmentForm'
import type {CopilotAssignmentBaseProps} from './copilot-assignment-types'
import {Banner, Dialog, Label, Link, type DialogProps} from '@primer/react'
import {useCallback, useRef, useMemo, useState, useEffect} from 'react'
import {assignIssuesToCopilot} from '../../utils/copilot-assignment-api'
import {formatAssignmentError, formatException, buildAssignmentFailureBaseMessage} from '../../utils/error-handling'
import {ScopedCommands} from '@github-ui/ui-commands'
import {isStaff} from '@github-ui/stats'
import styles from './CopilotAssignmentModal.module.css'
import {ConsecutiveCAPI401sProvider} from '../../contexts/ConsecutiveCAPI401sContext'
import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import {useCopilotLicenseType} from '../../hooks/use-copilot-license-type'
import {EntitlementProvider} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {CopilotCodingAgentQuotaBanner} from '@github-ui/copilot-chat/components/quota/CopilotCodingAgentQuotaBanner'
import type {SelectedAgent} from '../../types/session'

type OnSubmitHandler = (
  graphQLId: string,
  selectedBranch?: string,
  content?: string,
  selectedAgent?: SelectedAgent,
  selectedModel?: string,
  selectedRepoNwo?: string,
  selectedRepoId?: number,
) => void

// When onSubmit is provided, repository.id is required; otherwise it's optional
export interface CopilotAssignmentModalProps extends CopilotAssignmentBaseProps {
  isOpen: boolean
  onClose: () => void
  returnFocusRef?: DialogProps['returnFocusRef']
  issueNumbers?: number[]
  onSubmit?: OnSubmitHandler
  title?: string
  description?: string
  promptPlaceholder?: string
  promptLabelHidden?: boolean
  promptRequired?: boolean
}

/**
 * Modal component for Copilot-assisted issue assignment.
 * Uses CopilotAssignmentForm component for repo/branch selection and custom instructions.
 * Can be used independently of the button component.
 */
export function CopilotAssignmentModal({
  isOpen,
  onClose,
  repository,
  issueNumbers,
  onAssignmentComplete,
  returnFocusRef,
  onSubmit: customOnSubmit,
  title: customTitle,
  description: customDescription,
}: CopilotAssignmentModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const formRef = useRef<CopilotAssignmentFormRef>(null)
  const licenseType = useCopilotLicenseType()

  const [initialIssueCount, setInitialIssueCount] = useState(() => issueNumbers?.length ?? 0)
  const wasOpenRef = useRef(isOpen)

  // Only update initial count when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setInitialIssueCount(issueNumbers?.length ?? 0)
    }
    wasOpenRef.current = isOpen
  }, [isOpen, issueNumbers])

  const handleClose = () => {
    // Reset all state when modal is dismissed
    setErrorMessage(null)
    setIsSubmitting(false)
    onClose()
  }

  // Derive owner and repo from repository object
  const owner = repository.owner.login
  const repo = repository.name

  const multipleIssues = initialIssueCount > 1

  const handleSubmit = useCallback(
    async (
      content: string,
      selectedRepo: DropdownRepository | null,
      selectedBranch: string,
      selectedAgent: SelectedAgent,
      selectedModel: string | undefined,
    ) => {
      if (!selectedRepo) {
        return
      }

      if (customOnSubmit && selectedRepo.graphQLId) {
        setIsSubmitting(true)
        try {
          await customOnSubmit(
            selectedRepo.graphQLId,
            selectedBranch,
            content,
            selectedAgent,
            selectedModel,
            selectedRepo.nameWithOwner,
            selectedRepo.id,
          )
        } finally {
          setIsSubmitting(false)
        }
        return
      }

      if (!issueNumbers || issueNumbers.length === 0) {
        return
      }

      // Capture the issue numbers at submission time to make sure err message isn't changed by parent mutation
      const submittedIssueNumbers = [...issueNumbers]

      setIsSubmitting(true)
      setErrorMessage(null)
      try {
        // Make API call to Issues controller for Copilot assignment
        const response = await assignIssuesToCopilot(owner, repo, submittedIssueNumbers, {
          instructions: content,
          targetRepo: selectedRepo.nameWithOwner,
          targetBranch: selectedBranch,
          customAgent: selectedAgent?.subAgentSlug || undefined,
          agentId: selectedAgent?.topLevelAgentId || undefined,
          model: selectedModel,
        })

        if (response.ok) {
          await onAssignmentComplete?.(response.data?.copilot)
          onClose()
        } else {
          const baseMessage = multipleIssues
            ? buildAssignmentFailureBaseMessage(submittedIssueNumbers, response.jobs)
            : 'Assignment failed'
          const formattedError = formatAssignmentError(response, baseMessage)
          setErrorMessage(formattedError)
        }
      } catch (error) {
        const formattedError = formatException(error, 'Assignment')
        setErrorMessage(formattedError)
      } finally {
        setIsSubmitting(false)
      }
    },
    [issueNumbers, owner, repo, onAssignmentComplete, onClose, multipleIssues, customOnSubmit],
  )

  // Convert repository to the format expected by CopilotAssignmentForm
  const initialRepo = useMemo(
    () =>
      repository
        ? {
            name: repository.name,
            defaultBranch: undefined, // Default branch will be fetched by the form component
            owner: {
              login: repository.owner.login,
            },
          }
        : null,
    [repository],
  )

  const handleFormSubmitCommand = useCallback(async () => {
    // Only submit if form is not already submitting and form ref is available
    if (!isSubmitting && formRef.current) {
      await formRef.current.submit()
    }
  }, [isSubmitting])

  if (!isOpen) {
    return null
  }

  const defaultTitle = multipleIssues ? `Assign ${initialIssueCount} issues to Copilot` : 'Assign Copilot to issue'
  const title = customTitle ?? defaultTitle

  const defaultDescription = `Copilot will use the issue description, comments, and any additional prompt you provide to get started. Select a custom agent for specialized tasks.`
  const description = customDescription ?? defaultDescription

  // Staff vs external feedback link (placeholder for external until decided)
  const feedbackHref = isStaff()
    ? 'https://gh.io/copilot-issues-cross-repo-assign-staff-feedback'
    : 'https://gh.io/copilot-issues-cross-repo-assign-feedback'

  const copilotDialog = (
    <Dialog
      onClose={handleClose}
      width="xlarge"
      initialFocusRef={textareaRef}
      returnFocusRef={returnFocusRef}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header id={dialogLabelId} className={styles.header}>
          <Dialog.Title>{title}</Dialog.Title>
          <div className={styles.rightSection}>
            <Label variant="success" className={styles.previewBadge}>
              Preview
            </Label>
            <Link href={feedbackHref} target="_blank" rel="noopener noreferrer" className={styles.feedbackLink}>
              Feedback
            </Link>
            <Dialog.CloseButton onClose={handleClose} />
          </div>
        </Dialog.Header>
      )}
    >
      {errorMessage && (
        <div className={styles.errorContainer}>
          <Banner
            variant="critical"
            title="Assignment Failed"
            hideTitle
            description={
              <div data-testid="copilot-markdown">
                <MarkdownRenderer markdown={errorMessage} className={styles.errorMarkdown} />
              </div>
            }
            secondaryAction={
              <Banner.SecondaryAction onClick={() => setErrorMessage(null)}>Dismiss</Banner.SecondaryAction>
            }
          />
        </div>
      )}
      {/* CopilotAssignmentForm handles default branch fetching internally */}
      {initialRepo && (
        <ScopedCommands commands={{'github:submit-form': handleFormSubmitCommand}}>
          <ConsecutiveCAPI401sProvider>
            <EntitlementProvider initialLicenseType={licenseType}>
              <CopilotAssignmentModalContent
                formRef={formRef}
                initialRepo={initialRepo}
                onSubmit={handleSubmit}
                onCancel={handleClose}
                isSubmitting={isSubmitting}
                textareaRef={textareaRef}
                description={description}
              />
            </EntitlementProvider>
          </ConsecutiveCAPI401sProvider>
        </ScopedCommands>
      )}
    </Dialog>
  )

  return copilotDialog
}

/**
 * Inner component that renders the form inside EntitlementProvider.
 * Must be rendered inside EntitlementProvider.
 */
interface CopilotAssignmentModalContentProps {
  formRef: React.RefObject<CopilotAssignmentFormRef | null>
  initialRepo: {
    name: string
    defaultBranch: undefined
    owner: {login: string}
  }
  onSubmit: (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
    selectedModel: string | undefined,
  ) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
  description: string
}

function CopilotAssignmentModalContent({
  formRef,
  initialRepo,
  onSubmit,
  onCancel,
  isSubmitting,
  textareaRef,
  description,
}: CopilotAssignmentModalContentProps) {
  const [isQuotaExhausted, setIsQuotaExhausted] = useState(false)

  return (
    <>
      <CopilotCodingAgentQuotaBanner
        onQuotaExhaustedChange={setIsQuotaExhausted}
        entrypointLocation="ASSIGNMENT_MODAL"
      />
      <p className={styles.description}>{description}</p>
      <CopilotAssignmentForm
        ref={formRef}
        initialRepo={initialRepo}
        onSubmit={onSubmit}
        onCancel={onCancel}
        isSubmitting={isSubmitting}
        isQuotaExhausted={isQuotaExhausted}
        textareaRef={textareaRef}
        textareaAriaDescribedBy="copilot-assignment-description"
        insideDialog
      />
    </>
  )
}
